import { useState, useCallback, useEffect } from 'react';
import TableHead from '../table-head/table-head';
import { License, Prescription, TitleHead } from '../../types/data';
import Row from '../row/row';
import { DrawerComponent } from 'src/components/DialogComponent';
import PrescriptionForm from '../prescription-form/prescription-form';
import LicenseForm from '../license-form/license-form';
import Loading from 'src/components/Loading';
import { EmptyList } from 'src/components/EmptyList';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { naturalSort } from '../../utils/utils';

type SortConfig = {
  field: string | null;
  direction: 'asc' | 'desc' | null;
};

type HorizontalTableProps = {
  data: Prescription[] | License[];
  dataHead: TitleHead[];
  isLicenses?: boolean;
  isLoading?: boolean;
  hasMorePages?: boolean;
  onLoadMore?: () => void;
  filters?: {
    statusPrescription?: string[];
    statusLicanse?: string[];
    status?: string[];
    actualClosingDateRange?: {
      startDate?: string;
      endDate?: string;
    };
  };
};

function HorizontalTable({ data, dataHead, isLicenses, isLoading = false, hasMorePages = true, onLoadMore, filters }: HorizontalTableProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState<(Prescription & License) | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: null, direction: null });

  const [filteredData, setFilteredData] = useState<(Prescription | License)[]>([]);
  const observerTarget = useCallback((node: HTMLTableRowElement | null) => {
    if (isLoading) return;
    if (node && hasMorePages && onLoadMore) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMorePages) {
            onLoadMore();
          }
        },
        { threshold: 1.0 }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }
  }, [isLoading, hasMorePages, onLoadMore]);

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const modalId = isLicenses ? searchParams.get('licenseCard') : searchParams.get('prescriptionCard');

  const handleSort = useCallback((field: string) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.field === field) {
        if (prevConfig.direction === 'asc') {
          return { field, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { field: null, direction: null }; 
        }
      }
      return { field, direction: 'asc' };
    });
  }, []);

  useEffect(() => {
    let result = data.filter((item: any) => {
      if (isLicenses) {
        return item.documentNumber !== '-';
      } else {
        return item.prescriptionNumber !== '-';
      }
    });

    //! Фильтрация по статусу предписания
    if (filters?.statusPrescription && filters?.statusPrescription?.length > 0) {
      result = result.filter((item: any) => filters?.statusPrescription?.includes(item.status.name));
    }

    //! Фильтрация по статусу лицензии
    if (filters?.statusLicanse && filters?.statusLicanse?.length > 0) {
      result = result.filter((item: any) => filters?.statusLicanse?.includes(item.status.name));
    }

    //! Фильтрация по статусу действий (предписания и лицензии)
    if (filters?.status && filters?.status?.length > 0) {
      result = result.filter((item: any) => filters?.status?.includes(item.actionStatus));
    }

    //! Фильтрация по фактической дате закрытия

    if (filters?.actualClosingDateRange?.startDate && filters?.actualClosingDateRange?.endDate) {
      result = result.filter((item: any) => {
        if (!item.actualClosingDatePrescription?.startDate) return false;

        const itemDate = item.actualClosingDatePrescription.startDate;
        const itemDateStr = itemDate.toISOString().slice(0, 10);
        const startDate = filters?.actualClosingDateRange?.startDate ?? '';
        const endDate = filters?.actualClosingDateRange?.endDate ?? '';

        return itemDateStr >= startDate && itemDateStr <= endDate;
      });
    }

    result = result.filter((item, index, self) => self.findIndex((i) => i.id === item.id) === index);

    if (sortConfig.field && sortConfig.direction) {
      result.sort((a: any, b: any) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.field) {
          case 'prescriptionNumber':
            aValue = a.prescriptionNumber;
            bValue = b.prescriptionNumber;
            break;
          case 'dateVerification':
            aValue = a.dateVerification?.startDate?.getTime() ?? 0;
            bValue = b.dateVerification?.startDate?.getTime() ?? 0;
            break;
          case 'deadlineExecutionOrder':
            aValue = a.deadlineExecutionOrder?.startDate?.getTime() ?? 0;
            bValue = b.deadlineExecutionOrder?.startDate?.getTime() ?? 0;
            break;
          case 'actualClosingDatePrescription':
            aValue = a.actualClosingDatePrescription?.startDate?.getTime() ?? 0;
            bValue = b.actualClosingDatePrescription?.startDate?.getTime() ?? 0;
            break;
          case 'documentNumber':
            aValue = a.documentNumber;
            bValue = b.documentNumber;
            break;
          case 'startDateDocument':
            aValue = a.startDateDocument?.startDate?.getTime() ?? 0;
            bValue = b.startDateDocument?.startDate?.getTime() ?? 0;
            break;
          case 'expirationDateDocument':
            aValue = a.expirationDateDocument?.startDate?.getTime() ?? 0;
            bValue = b.expirationDateDocument?.startDate?.getTime() ?? 0;
            break;
          default:
            return 0;
        }

        let comparison = 0;
        if (sortConfig.field === 'prescriptionNumber' || sortConfig.field === 'documentNumber') {
          comparison = naturalSort(aValue, bValue);
        } else {
          comparison = aValue - bValue;
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredData(result);
  }, [data, filters, isLicenses, sortConfig]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setSearchParams({});
  }, []);

  const handleRowClick = (data: any) => {
    isLicenses ? navigate(`?licenseCard=${data.id}`) : navigate(`?prescriptionCard=${data.id}`);
    setModalData(data);
    setIsOpen(true);
  };

  useEffect(() => {
    if (modalId && data.length > 0) {
      const currentEl = data.find((el) => el.id === Number(modalId));
      if(currentEl){
        setModalData(currentEl);
        setIsOpen(true);
      }
    }
  }, [modalId, data]);

  return (
    <div className='flex flex-col h-[calc(90vh-110px)]'>
      <div className='w-[100%] overflow-x-auto h-[calc(90vh-110px)] overflow-y-auto scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] shadow bg-white rounded-lg px-6 py-5 text-sm'>
        <table className='w-[100%] table-fixed border-collapse'>
          <TableHead data={dataHead} isLicenses={isLicenses} onSort={handleSort} sortConfig={sortConfig} />

          <tbody>
            {filteredData
              .filter((item, index, self) => self.findIndex((i) => i.id === item.id) === index)
              .map((item, index, array) => (
                <Row key={item.id} data={{ ...item }} onRowClick={handleRowClick} />
              ))}
            {hasMorePages && filteredData.length > 0 && (
              <tr ref={observerTarget}>
                <td colSpan={dataHead.length} className='h-1'></td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={dataHead.length} className='text-center py-4'>
                  <div className='flex items-center justify-center bg-white'>
                    <Loading />
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && filteredData.length === 0 && (
              <tr>
                <td colSpan={dataHead.length} className='text-center py-4'>
                  <EmptyList title='Данные не найдены' />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!isLoading && filteredData.length > 0 && !hasMorePages && <div className='bg-[#012169] text-white text-center py-2 mt-2'>Список сформирован</div>}

      {isLicenses ? (
        <DrawerComponent open={isOpen} zIndex={50} toggleDrawer={closeModal}>
          <LicenseForm isEdit initialValues={modalData} toggleDoing={closeModal} />
        </DrawerComponent>
      ) : (
        <DrawerComponent open={isOpen} zIndex={50} toggleDrawer={closeModal}>
          <PrescriptionForm isEdit initialValues={modalData} toggleDoing={closeModal} />
        </DrawerComponent>
      )}
    </div>
  );
}

export default HorizontalTable;
